import { Navigate, useLocation, type RouteObject } from 'react-router';
import { INVITATION_PATH, PRODUCT_KEYS } from '@traceio-org/platform-contracts';
import { loadBootstrap } from './app/bootstrap';
import { ROOT_ROUTE_ID } from './app/context';
import type { HubDependencies } from './app/deps';
import { CallbackPage } from './pages/CallbackPage';
import { InvitationPage } from './pages/InvitationPage';
import { LoginPage } from './pages/LoginPage';
import { SelectOrganizationPage } from './pages/SelectOrganizationPage';
import { SelectProductPage } from './pages/SelectProductPage';
import { BootLoading, RootError, RootLayout } from './pages/ShellPages';
import { SignupPage } from './pages/SignupPage';
import { settingsRoutes } from './settings/routes';
import { startRoutes } from './start/routes';
import { ticketRoutes } from './tickets/routes';
import {
  AccessDeniedPage,
  LogoutPage,
  NoWorkspacePage,
  NotFoundPage,
  SessionExpiredPage,
  SignedOutPage,
  UnavailablePage,
} from './pages/StatusPages';

/** "/" goes to "/login", keeping the query string. */
function RootRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/login', search: location.search }} replace />;
}

/**
 * The hub's routes (exact paths). The root loader resolves the host and loads the registry once per
 * page load; pages read them with useBootstrap.
 */
export function createHubRoutes(deps: HubDependencies): RouteObject[] {
  return [
    {
      id: ROOT_ROUTE_ID,
      path: '/',
      loader: () => loadBootstrap(deps),
      // Host and registry do not change while the page is open.
      shouldRevalidate: () => false,
      element: <RootLayout />,
      hydrateFallbackElement: <BootLoading />,
      errorElement: <RootError />,
      children: [
        { index: true, element: <RootRedirect /> },
        { path: 'login', element: <LoginPage productKey={null} /> },
        ...PRODUCT_KEYS.map((productKey) => ({
          path: `login/${productKey}`,
          element: <LoginPage key={productKey} productKey={productKey} />,
        })),
        // Self-serve sign-up (spec 2026-10-18 piece 3): HUB_PATHS.signup, without its leading slash.
        { path: 'signup', element: <SignupPage /> },
        { path: 'auth/callback', element: <CallbackPage /> },
        { path: 'select-organization', element: <SelectOrganizationPage /> },
        { path: 'select-product', element: <SelectProductPage /> },
        { path: 'access-denied', element: <AccessDeniedPage /> },
        { path: 'no-workspace', element: <NoWorkspacePage /> },
        { path: 'unavailable', element: <UnavailablePage /> },
        // The contract path every invitation link is built from (invitationUrl), without its leading slash.
        { path: INVITATION_PATH.slice(1), element: <InvitationPage /> },
        { path: 'signed-out', element: <SignedOutPage /> },
        { path: 'session-expired', element: <SessionExpiredPage /> },
        { path: 'logout', element: <LogoutPage /> },
        // Organization settings: owners, administrators and billing administrators of a customer organization.
        ...settingsRoutes(),
        // Tickets: My tickets, a new ticket and one ticket, for every signed-in member.
        ...ticketRoutes(),
        // Start your trial: self-serve sign-up for a founder with no organization yet.
        ...startRoutes(),
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}
